/* eslint-disable no-mixed-spaces-and-tabs */
import React, { useEffect, useMemo, useState } from 'react';
import { getDealChipProps } from '@/utils/objectChip';
import { useDispatch, useSelector } from 'react-redux';
import {
	TextField,
	InputAdornment,
	IconButton,
	CircularProgress,
	Chip,
	Tooltip,
	Box,
	Stack,
	Skeleton,
	Grid,
	useTheme,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import CheckIcon from '@mui/icons-material/Check';
import { useGetUserDetailsQuery } from '@/features/user/userApi';
import { IS_DEV } from '@/utils/environment';
import { InstantSearch, Configure, useHits, useSearchBox, Hits } from 'react-instantsearch';
import { dealSearch } from '@/utils/typesense/clients/dealSearch';
import { useBatchGetDealListPoliciesQuery } from '@/features/deals/dealsApi';
import { usePipelineContext } from '@/context/pipelineContext';
import { getClientIdFilterFromId } from '@/features/clients/helper';

const AutocompleteInput = ({
	query,
	refine,
	clear,
	hits,
	isSearching,
	setDeal,
	dealIds = [],
	disabled,
	showRenewable,
	autoPopulate = false,
	multiple = false,
	disabledDealIds = [],
	loading = false,
}) => {
	const theme = useTheme();
	const [open, setOpen] = useState(false);
	const [resetKey, setResetKey] = useState(0);

	const [values, setValues] = useState(() => hits.filter((d) => dealIds.includes(d.id)));

	const { active, ...pipelineQuery } = usePipelineContext();

	const updatedHits = useMemo(() => {
		return hits.map((h) => ({
			...h,
			active: active.some((s) => s.id == h.dealStage && s.pipelineId == h.pipeline),
		}));
	}, [hits, active]);

	const handleSearch = () => {
		if (query.length > 0) {
			refine();
		}
	};

	const handleReset = () => {
		setDeal(null);
		setOpen(false);
		clear();
	};

	const dealName = hits.find((hit) => hit.id == dealIds[0])?.dealName ?? '';

	useEffect(() => {
		if (!multiple && (dealIds.length === 1 || dealIds.length === 0)) {
			refine(dealName);
		}
		setValues(hits.filter((d) => dealIds.includes(d.id)));
	}, [dealIds]);

	useEffect(() => {
		if (values.length === 0) {
			setResetKey((prev) => prev + 1);
		}
	}, [values]);

	return (
		<Autocomplete
			id='deal-select'
			key={`deal-select-${resetKey}`}
			loading={loading || isSearching || pipelineQuery.isLoading}
			multiple={multiple}
			loadingText='Loading deals...'
			options={autoPopulate ? updatedHits : query.length > 0 ? updatedHits : []}
			open={open}
			disabled={disabled}
			noOptionsText={query.length > 0 ? 'No deals / claims found' : 'Type to search'}
			filterOptions={(x) => x}
			renderOption={(props, option) => {
				const dealChipProps = getDealChipProps(option.pipeline, theme);
				return (
					<Box
						display={'flex'}
						{...props}
						sx={{
							...props.sx,
							alignItems: 'center',
							justifyContent: 'space-between',
						}}
						key={`deal-select-${option.id}`}
					>
						<Box flexGrow={1} textAlign='left'>
							<Stack direction='row' alignItems={'center'}>
								<Chip
									size='small'
									variant='outlined'
									style={{
										borderColor: dealChipProps.borderColor,
										color: dealChipProps.color,
									}}
									label={dealChipProps.label}
									sx={{
										mr: '1em',
									}}
								/>
								{`${option.dealName} ${
									option?.fileMigrationInProgress
										? `(${option.clientName} file migration in progress)`
										: ''
								}`}
								{showRenewable &&
									(disabledDealIds.includes(option.id) ||
										option.active ||
										(option.policies ?? []).length === 0) && (
										<span
											style={{
												paddingLeft: '0.25em',
												fontSize: '12px',
												color: theme.palette.text.secondary,
											}}
										>
											{(option.policies ?? []).length === 0
												? '(no policies linked)'
												: '(in progress)'}
										</span>
									)}
							</Stack>
						</Box>
						{dealIds.includes(option.objectID) && (
							<CheckIcon sx={{ fontSize: '18px' }} />
						)}
					</Box>
				);
			}}
			inputValue={query}
			onOpen={() => setOpen(true)}
			onClose={() => setOpen(false)}
			getOptionLabel={(option) => option.dealName}
			onInputChange={(_, value, reason) => {
				if (reason !== 'reset') {
					refine(value);
				}
			}}
			renderInput={(params) => (
				<TextField
					placeholder={
						multiple || dealIds.length === 0
							? 'Search for deal'
							: hits.find((hit) => hit.id === dealIds[0])?.dealName
					}
					{...params}
					onKeyDown={(e) => {
						if (e.key === 'Enter') {
							e.preventDefault();
							handleSearch();
						}
					}}
					InputProps={{
						...params.InputProps,
						startAdornment: (
							<InputAdornment position='start'>
								<IconButton
									disabled={isSearching || disabled}
									sx={{ padding: '0' }}
									onClick={handleSearch}
								>
									<SearchIcon />
								</IconButton>
							</InputAdornment>
						),
						endAdornment: (
							<InputAdornment position='end'>
								{isSearching ? (
									<CircularProgress color='inherit' size={20} />
								) : null}
								{query.length > 0 && (
									<IconButton
										size='small'
										disabled={isSearching || disabled}
										sx={{ padding: '0' }}
										onClick={handleReset}
									>
										<ClearIcon fontSize='20px' />
									</IconButton>
								)}
							</InputAdornment>
						),
					}}
				/>
			)}
			isOptionEqualToValue={(option, value) => option.id === value.id}
			onChange={(_, value) => {
				console.log('value', value);
				setDeal(value);
			}}
			getOptionDisabled={(option) =>
				option?.fileMigrationInProgress
					? true
					: showRenewable
						? disabledDealIds.includes(option.id) ||
							option.active ||
							(option.policies ?? []).length === 0
						: false
			}
			{...(multiple && { value: values })}
		/>
	);
};

export const InstantDealSelect = ({
	setDeal,
	dealId,
	disabled,
	clientId,
	autoPopulate,
	showRenewable,
	displayClaims = true,
	multiple = false,
	disabledDealIds = [],
	hideInput = false,
}) => {
	// const [isTyping, setIsTyping] = useState(false);
	const account = useSelector((state) => state.msalAccount.account);
	const email = account?.username;

	const dealIds = Array.isArray(dealId) ? dealId : dealId != null ? [dealId] : [];

	const userQuery = useGetUserDetailsQuery(
		{ email },
		{
			skip: !email,
		},
	);

	const brokerGroupIds = userQuery.data?.brokerGroupArray ?? [];

	const filterSchema = useMemo(() => {
		const clientIdFilter = getClientIdFilterFromId(clientId);
		const ownerFilter = `brokerGroupId: [${brokerGroupIds.join(',')}]`;

		const dealIdFilter =
			(hideInput || !multiple) && dealIds.length > 0 ? `dealId: [${dealIds.join(',')}]` : ''; //dealId ? `dealId := ${dealId}` : '';

		const schema = {
			filters: clientId ? `${clientIdFilter} && ${ownerFilter}` : ownerFilter,
		};

		if (!displayClaims) {
			schema.filters += ` && pipeline :!= "${process.env.REACT_APP_PIPELINE_CLAIMS}"`;
		}

		// if (dealId && !isTyping) {
		if (dealIdFilter.length > 0) {
			// console.log('DEAL ID FILTER', dealIdFilter);
			schema.filters += ` && ${dealIdFilter}`;
			// schema.filters = clientId ? `${clientIdFilter} && ${dealIdFilter}` : dealIdFilter;
		}

		// console.log('FILTER SCHEMA', schema);
		return schema;
	}, [clientId, brokerGroupIds, dealIds, hideInput]);

	return (
		<InstantSearch searchClient={dealSearch} indexName='deals'>
			<Configure hitsPerPage={25} {...filterSchema} />
			<InstantDealSelectWrapper
				autoPopulate={autoPopulate}
				setDeal={setDeal}
				dealIds={dealIds}
				disabled={disabled || userQuery.isLoading}
				disabledDealIds={disabledDealIds}
				clientId={clientId}
				// setIsTyping={setIsTyping}
				showRenewable={showRenewable}
				multiple={multiple}
				hideInput={hideInput}
			/>
		</InstantSearch>
	);
};

const InstantDealSelectWrapper = ({
	setDeal,
	dealIds,
	disabled,
	clientId,
	setIsTyping,
	autoPopulate,
	showRenewable,
	multiple = false,
	disabledDealIds = [],
	hideInput = false,
	...props
}) => {
	const { items } = useHits(props);

	const hits = items.map((hit) => ({
		...hit,
		docId: hit.id,
		objectType:
			hit.pipeline === process.env.REACT_APP_PIPELINE_CLAIMS ||
			(IS_DEV && hit.pipeline === process.env.REACT_APP_PIPELINE_ENDORSEMENTS)
				? 'ticket'
				: 'deal',
	}));

	const dealIdsForPolicies = useMemo(() => hits.map((h) => h.id), [hits]);

	const policiesQuery = useBatchGetDealListPoliciesQuery(
		{ dealIds: dealIdsForPolicies, waitForRetrieval: true },
		{
			skip:
				hideInput || !clientId || dealIdsForPolicies.length === 0 || showRenewable !== true,
		},
	);

	const loading =
		hideInput || !clientId || dealIdsForPolicies.length === 0 || showRenewable !== true
			? false
			: policiesQuery.isUninitialized || policiesQuery.isLoading || policiesQuery.isFetching;

	const dealsData = policiesQuery.data?.policiesByDealId ?? {};

	const updatedHits = useMemo(() => {
		const hhhits = hits.map((d) => ({
			...d,
			policies: dealsData?.[d.id] ?? [],
			isRenewable: true, //d.isRenewable == null || d.isRenewable == true,
		}));
		// console.log('DEAL SELECT HITS', hhhits);
		return hhhits;
	}, [hits, dealsData]);

	const selectedDeals = useMemo(
		() => updatedHits.filter((hit) => dealIds.includes(hit.id)),
		[updatedHits, dealIds],
	);

	// useEffect(() => console.log('SELECTED DEALS', selectedDeals), [selectedDeals]);

	const searchBoxApi = useSearchBox(props);

	// useEffect(() => {
	// 	if (searchBoxApi.query.length > 0) {
	// 		setIsTyping(true);
	// 	} else {
	// 		setIsTyping(false);
	// 	}
	// }, [searchBoxApi.query, setIsTyping]);

	// Cannot remove deal if the id is in the disabledDealIds array
	const handleRemoveDeal = (dealId) => {
		const newIds = Array.from(new Set([...dealIds.filter((id) => id != dealId)]));
		setDeal(updatedHits.filter((hit) => newIds.includes(hit.id)));
	};

	const handleSetDeal = (newValue) => {
		console.log('NEW VALUE', newValue);
		if (multiple) {
			const newVals = Array.isArray(newValue) ? newValue.map((d) => d.id) : [newValue.id];
			const newIds = Array.from(new Set([...dealIds, ...newVals]));
			const toRemove = dealIds.filter(
				(id) => !newVals.includes(id) && !disabledDealIds.includes(id),
			);
			if (toRemove.length > 0) {
				toRemove.forEach((id) => handleRemoveDeal(id));
			} else {
				setDeal(updatedHits.filter((hit) => newIds.includes(hit.id)));
			}
		} else {
			setDeal(newValue);
		}
	};

	return (
		<Stack width={'100%'}>
			{multiple && selectedDeals.length > 0 && (
				<Grid container spacing={1} py={'1em'}>
					{selectedDeals.map((d) => (
						<Chip
							key={`selected-deal-${d.dealId}`}
							size={'small'}
							// color={colour}
							// sx={{
							//     backgroundColor: colour,
							//     color: 'rgb(255, 255, 255)'
							// }}
							label={d.dealName}
							{...(!disabledDealIds.includes(d.dealId) && {
								onDelete: () => handleRemoveDeal(d.dealId),
							})}
						/>
					))}
				</Grid>
			)}
			{!hideInput && (
				<>
					{loading ? (
						<Skeleton width={'100%'} height={'48px'} />
					) : (
						<AutocompleteInput
							hits={updatedHits}
							setDeal={handleSetDeal}
							dealIds={dealIds}
							disabled={disabled}
							autoPopulate={autoPopulate}
							showRenewable={showRenewable}
							multiple={multiple}
							disabledDealIds={disabledDealIds}
							// loading={loading}
							{...searchBoxApi}
						/>
					)}
				</>
			)}
		</Stack>
	);
};
